import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings } from 'lucide-react';
import axios from 'axios';
import { calculateKarma, calculateGates, evaluateFormula, evaluateFormulaPair, sumKarmaResults } from '../utils/karmaCalc';

// Attach the pair evaluator as a property so KarmaMatrix can call evaluateFormula.pair(...)
evaluateFormula.pair = evaluateFormulaPair;
import KarmaMatrix from '../components/KarmaMatrix';
import KarmaTable from '../components/KarmaTable';
import AIInterpretation from '../components/AIInterpretation';
import ChallengeInterpretation from '../components/ChallengeInterpretation';
import ContactButtons from '../components/ContactButtons';
import SupportChat from '../components/SupportChat';
import GridCellsRenderer from '../components/GridCellsRenderer';
import EnergyCalculator from '../components/EnergyCalculator';
import VariantsSection from '../components/VariantsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Build version baked into THIS JS bundle. Must match BUILD_VERSION in backend
// (bumped on every deploy). The runtime version-sync check below compares this
// against the server's current version and forces a hard reload on mismatch —
// the only reliable cure for CDN edge nodes or browser disk caches that are
// serving a stale JS bundle long after a deploy.
const CLIENT_BUILD_VERSION = 'v14.0-202605200000';

const DEFAULT_CONNECTIONS = [
  ['B','D'],['B','Zh'],['B','Z'],['D','Zh'],['D','I'],['D','A'],
  ['Zh','Z'],['Zh','I'],['Zh','L'],['Zh','G'],['Z','L'],['Z','V'],
  ['I','G'],['I','M'],['I','A'],['L','G'],['L','V'],['G','M'],['G','K'],
  ['M','A'],['M','K'],['A','K'],['A','E'],['K','V'],['K','E'],['V','E']
];

const KarmaCalculator = ({ pageMode = 'main' }) => {
  const { slug: urlSlug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [dateInput, setDateInput] = useState('');
  const [results, setResults] = useState(null);
  // Optional compatibility mode: 2nd birth date + its computed results
  const [compatEnabled, setCompatEnabled] = useState(false);
  const [dateInput2, setDateInput2] = useState('');
  const [results2, setResults2] = useState(null);
  const [birthDate2, setBirthDate2] = useState(null);
  const [error, setError] = useState('');

  // Dynamic config from backend
  const [sectionOrder, setSectionOrder] = useState(['matrix', 'table', 'special']);
  const [tableRowOrder, setTableRowOrder] = useState([0, 1, 2, 3]);
  const [connections, setConnections] = useState(DEFAULT_CONNECTIONS);
  const [nodeLabels, setNodeLabels] = useState(null);
  const [nodePositions, setNodePositions] = useState(null);
  const [nodeColors, setNodeColors] = useState(null);
  const [nodeTextColors, setNodeTextColors] = useState(null);
  const [nodeSizes, setNodeSizes] = useState({});
  const [nodeShapes, setNodeShapes] = useState({});
  const [globalSphereSize, setGlobalSphereSize] = useState(22);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundScale, setBackgroundScale] = useState(1);
  const [backgroundOffsetX, setBackgroundOffsetX] = useState(0);
  const [backgroundOffsetY, setBackgroundOffsetY] = useState(0);
  const [backgroundLayers, setBackgroundLayers] = useState([]);
  const [sunX, setSunX] = useState(50);
  const [sunY, setSunY] = useState(-90);
  const [sunScale, setSunScale] = useState(1);
  const [sunBrightness, setSunBrightness] = useState(1);
  const [connectionSpeed, setConnectionSpeed] = useState(1);
  const [connectionStyle, setConnectionStyle] = useState('straight');
  const [title, setTitle] = useState('Karma Matrisi Kalkulyatoru');
  const [subtitle, setSubtitle] = useState('by Karma Akademi Canada');
  const [calculateButtonText, setCalculateButtonText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [footerX, setFooterX] = useState(50);
  const [footerY, setFooterY] = useState(95);
  const [footerVisible, setFooterVisible] = useState(true);
  const [footerFontSize, setFooterFontSize] = useState(14);
  const [footerColor, setFooterColor] = useState('#cbd5e1');
  const [challenges, setChallenges] = useState([]);
  const [challengeSectionTitle, setChallengeSectionTitle] = useState({ az: 'Uşaqlıq Kodu', en: 'Childhood Code', tr: 'Çocukluk Kodu', ru: 'Код Детства' });
  const [challengesEnabled, setChallengesEnabled] = useState(true);
  const [hideGroupsBar, setHideGroupsBar] = useState(false);
  const [hideSidePanels, setHideSidePanels] = useState(false);
  const [sumEnabled, setSumEnabled] = useState(false);
  const [sumReduce22, setSumReduce22] = useState(true);
  const [compatVisible, setCompatVisible] = useState(true);
  const [groupsPosition, setGroupsPosition] = useState('below-grid');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [whatsappLabel, setWhatsappLabel] = useState('WhatsApp');
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [instagramUsername, setInstagramUsername] = useState('');
  const [instagramEnabled, setInstagramEnabled] = useState(false);
  const [telegramUsername, setTelegramUsername] = useState('');
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [supportChatEnabled, setSupportChatEnabled] = useState(false);
  const [supportWelcomeMessage, setSupportWelcomeMessage] = useState('');
  const [connectionGroups, setConnectionGroups] = useState([]);
  // Per-session toggle overrides (id -> bool); null = use server-defined enabled
  const [groupOverrides, setGroupOverrides] = useState({});
  const [panels, setPanels] = useState({ left: null, right: null });
  const [backgroundFullscreen, setBackgroundFullscreen] = useState(true);
  const [paradiseMode, setParadiseMode] = useState(false);
  const [hiddenSpheres, setHiddenSpheres] = useState([]);
  const [hiddenValues, setHiddenValues] = useState([]);
  // Per-session panel overrides (left/right -> bool)
  const [panelOverrides, setPanelOverrides] = useState({});
  const [gates, setGates] = useState(null);
  const [sections, setSections] = useState(null);
  const [hiddenRows, setHiddenRows] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [customRows, setCustomRows] = useState([]);
  const [customNodes, setCustomNodes] = useState([]);
  const [gridCells, setGridCells] = useState([]);
  const [gridBgImage, setGridBgImage] = useState('');
  const [gridTitle, setGridTitle] = useState('');
  const [energyCalc, setEnergyCalc] = useState(null);
  const [gridPopupsEnabled, setGridPopupsEnabled] = useState(true);
  const [variants, setVariants] = useState([]);
  const [variantsLayout, setVariantsLayout] = useState('stacked');
  const [birthDate, setBirthDate] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(true);
  // Guard the page render until the public config has arrived from the API,
  // so users never see the React default state ("Karma Matrisi Kalkulyatoru" /
  // "Calculate Matrix") flash before re-rendering with the admin-saved values.
  const [configReady, setConfigReady] = useState(false);

  // Version sync check — runs FIRST. If the backend's current BUILD_VERSION
  // differs from the version baked into this JS bundle, the user is running a
  // stale cached bundle (CDN edge / browser disk cache). Force a hard reload
  // with a cache-busting query so the browser fetches fresh assets. Bounded by
  // a sessionStorage flag so we never loop.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (sessionStorage.getItem('km_version_reload')) return;
        const res = await axios.get(`${API}/version`, { timeout: 5000 });
        const serverVersion = res?.data?.version;
        if (!serverVersion || cancelled) return;
        if (serverVersion !== CLIENT_BUILD_VERSION) {
          sessionStorage.setItem('km_version_reload', '1');
          const url = new URL(window.location.href);
          url.searchParams.set('_v', serverVersion);
          window.location.replace(url.toString());
        } else {
          // Versions match — clear the loop-guard so a future mismatch can fire
          sessionStorage.removeItem('km_version_reload');
        }
      } catch (e) { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch config on mount.
  // In `student` page mode we attach the student JWT and request ?page=student
  // so the backend returns the per-page config. If the token is missing or
  // expired, redirect to /student/login.
  const configRequestParams = () => {
    if (pageMode === 'user') {
      const slug = (urlSlug || '').trim();
      if (!slug) return { params: {} };
      return { params: { page: `user_${slug}` } };
    }
    if (pageMode !== 'student') return { params: {} };
    const token = localStorage.getItem('student_token');
    const exp = parseInt(localStorage.getItem('student_token_exp') || '0', 10);
    if (!token || !exp || Date.now() >= exp) {
      navigate('/student/login', { replace: true });
      return null;
    }
    return {
      params: { page: 'student' },
      headers: { Authorization: `Bearer ${token}` },
    };
  };

  useEffect(() => {
    const reqOpts = configRequestParams();
    if (!reqOpts) return;
    axios.get(`${API}/config`, reqOpts)
      .then(res => {
        if (res.data.section_order) setSectionOrder(res.data.section_order);
        if (res.data.table_row_order) setTableRowOrder(res.data.table_row_order);
        if (res.data.connections) setConnections(res.data.connections);
        if (res.data.node_labels) setNodeLabels(res.data.node_labels);
        if (res.data.node_positions) setNodePositions(res.data.node_positions);
        if (res.data.node_colors) setNodeColors(res.data.node_colors);
        if (res.data.node_text_colors) setNodeTextColors(res.data.node_text_colors);
        if (res.data.node_sizes) setNodeSizes(res.data.node_sizes);
        if (res.data.node_shapes) setNodeShapes(res.data.node_shapes);
        if (res.data.global_sphere_size) setGlobalSphereSize(res.data.global_sphere_size);
        if (res.data.background_image) setBackgroundImage(res.data.background_image);
        if (res.data.background_scale !== undefined) setBackgroundScale(res.data.background_scale);
        if (res.data.background_offset_x !== undefined) setBackgroundOffsetX(res.data.background_offset_x);
        if (res.data.background_offset_y !== undefined) setBackgroundOffsetY(res.data.background_offset_y);
        if (res.data.background_layers) setBackgroundLayers(res.data.background_layers);
        if (res.data.sun_x !== undefined) setSunX(res.data.sun_x);
        if (res.data.sun_y !== undefined) setSunY(res.data.sun_y);
        if (res.data.sun_scale !== undefined) setSunScale(res.data.sun_scale);
        if (res.data.sun_brightness !== undefined) setSunBrightness(res.data.sun_brightness);
        if (res.data.connection_speed !== undefined) setConnectionSpeed(res.data.connection_speed);
        if (res.data.connection_style) setConnectionStyle(res.data.connection_style);
        if (res.data.title) setTitle(res.data.title);
        if (res.data.subtitle !== undefined) setSubtitle(res.data.subtitle);
        if (res.data.calculate_button_text !== undefined) setCalculateButtonText(res.data.calculate_button_text);
        if (res.data.footer_text !== undefined) setFooterText(res.data.footer_text);
        if (res.data.footer_x !== undefined) setFooterX(res.data.footer_x);
        if (res.data.footer_y !== undefined) setFooterY(res.data.footer_y);
        if (res.data.footer_visible !== undefined) setFooterVisible(res.data.footer_visible);
        if (res.data.footer_font_size !== undefined) setFooterFontSize(res.data.footer_font_size);
        if (res.data.footer_color !== undefined) setFooterColor(res.data.footer_color);
        if (Array.isArray(res.data.challenges)) setChallenges(res.data.challenges);
        if (res.data.challenge_section_title) setChallengeSectionTitle(res.data.challenge_section_title);
        if (res.data.challenges_enabled !== undefined) setChallengesEnabled(res.data.challenges_enabled);
        if (res.data.hide_groups_bar !== undefined) setHideGroupsBar(res.data.hide_groups_bar);
        if (res.data.hide_side_panels !== undefined) setHideSidePanels(res.data.hide_side_panels);
        if (res.data.whatsapp_number !== undefined) setWhatsappNumber(res.data.whatsapp_number);
        if (res.data.whatsapp_message !== undefined) setWhatsappMessage(res.data.whatsapp_message);
        if (res.data.whatsapp_label !== undefined) setWhatsappLabel(res.data.whatsapp_label);
        if (res.data.whatsapp_enabled !== undefined) setWhatsappEnabled(res.data.whatsapp_enabled);
        if (res.data.instagram_username !== undefined) setInstagramUsername(res.data.instagram_username);
        if (res.data.instagram_enabled !== undefined) setInstagramEnabled(res.data.instagram_enabled);
        if (res.data.telegram_username !== undefined) setTelegramUsername(res.data.telegram_username);
        if (res.data.telegram_enabled !== undefined) setTelegramEnabled(res.data.telegram_enabled);
        if (res.data.email_address !== undefined) setEmailAddress(res.data.email_address);
        if (res.data.email_subject !== undefined) setEmailSubject(res.data.email_subject);
        if (res.data.email_enabled !== undefined) setEmailEnabled(res.data.email_enabled);
        if (res.data.support_chat_enabled !== undefined) setSupportChatEnabled(res.data.support_chat_enabled);
        if (res.data.support_welcome_message !== undefined) setSupportWelcomeMessage(res.data.support_welcome_message);
        if (res.data.connection_groups) setConnectionGroups(res.data.connection_groups);
        if (res.data.panels) setPanels(res.data.panels);
        if (res.data.background_fullscreen !== undefined) setBackgroundFullscreen(res.data.background_fullscreen);
        if (res.data.paradise_mode !== undefined) setParadiseMode(res.data.paradise_mode);
        if (Array.isArray(res.data.hidden_spheres)) setHiddenSpheres(res.data.hidden_spheres);
        if (Array.isArray(res.data.hidden_values)) setHiddenValues(res.data.hidden_values);
        if (res.data.gates) setGates(res.data.gates);
        if (res.data.sections) setSections(res.data.sections);
        if (res.data.hidden_rows) setHiddenRows(res.data.hidden_rows);
        if (res.data.hidden_columns) setHiddenColumns(res.data.hidden_columns);
        if (res.data.custom_rows) setCustomRows(res.data.custom_rows);
        if (res.data.custom_nodes) setCustomNodes(res.data.custom_nodes);
        if (res.data.grid_cells) setGridCells(res.data.grid_cells);
        if (res.data.grid_bg_image !== undefined) setGridBgImage(res.data.grid_bg_image || '');
        if (res.data.grid_title !== undefined) setGridTitle(res.data.grid_title || '');
        setEnergyCalc(res.data.energy_calc || null);
        if (res.data.grid_popups_enabled !== undefined) setGridPopupsEnabled(res.data.grid_popups_enabled !== false);
        if (Array.isArray(res.data.variants)) setVariants(res.data.variants);
        if (res.data.variants_layout) setVariantsLayout(res.data.variants_layout);
        if (res.data.ai_enabled !== undefined) setAiEnabled(res.data.ai_enabled);
        if (res.data.sum_enabled !== undefined) setSumEnabled(res.data.sum_enabled);
        if (res.data.sum_reduce22 !== undefined) setSumReduce22(res.data.sum_reduce22);
        if (res.data.compat_visible !== undefined) setCompatVisible(res.data.compat_visible !== false);
        if (res.data.groups_position !== undefined) setGroupsPosition(res.data.groups_position || 'below-grid');
      })
      .catch((err) => {
        // 401 on student page → token expired, send back to login
        if (pageMode === 'student' && err?.response?.status === 401) {
          localStorage.removeItem('student_token');
          localStorage.removeItem('student_token_exp');
          navigate('/student/login', { replace: true });
        }
      })
      .finally(() => {
        // Reveal the page only after the config is in place (success OR failure).
        // On failure we still reveal so users aren't stuck on a black screen.
        setConfigReady(true);
      });

    // Record this visit on fresh page load (not on bfcache restore — bfcache
    // restored pages already had their visit logged on the original load).
    // Use a session-scoped flag so SPA navigation doesn't double-count.
    try {
      if (typeof window !== 'undefined' && !window.__karma_visit_recorded && pageMode !== 'student') {
        window.__karma_visit_recorded = true;
        const visitParams = {};
        if (pageMode === 'user' && urlSlug) {
          visitParams.page = `user_${urlSlug}`;
          visitParams.slug = urlSlug;
        } else if (pageMode === 'student') {
          visitParams.page = 'student';
        } else {
          visitParams.page = 'main';
        }
        axios.post(`${API}/visit`, null, { params: visitParams }).catch(() => {});
      }
    } catch (e) {
      // best-effort, never break the app
    }
  }, []);

  // iOS Safari bfcache: when the user navigates back to the site, Safari may
  // restore a cached snapshot of the previous page state. The loading veil
  // (configReady gate) already covers this scenario because on bfcache restore
  // the cached snapshot will paint, but our config fetch fires again from
  // `pageshow` and the veil is shown until fresh data arrives. We also register
  // a no-op `unload` listener which disqualifies the page from bfcache entirely
  // per the WebKit spec, so future visits get a true fresh load.
  useEffect(() => {
    const noopUnload = () => {};
    window.addEventListener('unload', noopUnload);

    const onPageShow = (event) => {
      if (event.persisted) {
        // Restored from bfcache — re-hide the page and refetch config so the
        // user never sees stale calculation state from the prior session.
        setConfigReady(false);
        setResults(null);
        setBirthDate(null);
        setDateInput('');
        setError('');
        axios.get(`${API}/config`, configRequestParams() || {}).catch(() => {}).finally(() => setConfigReady(true));
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('unload', noopUnload);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  // Generate random stars for background. Use fewer on touch / small screens.
  // Stars background — pre-existing decoration. Math.random in useMemo is fine
  // here because we only want randomization once per mount.
  const stars = useMemo(() => {
    const isTouch = typeof window !== 'undefined' &&
      (window.matchMedia('(hover: none) and (pointer: coarse)').matches || window.innerWidth < 768);
    const count = isTouch ? 60 : 180;
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 0.5,
      delay: Math.random() * 5,
      duration: Math.random() * 3 + 2,
      opacity: Math.random() * 0.6 + 0.2,
    }));
  }, []);

  const handleDateChange = useCallback((e) => {
    const raw = e.target.value;
    const cleaned = raw.replace(/[^\d]/g, '');
    let formatted = '';
    if (cleaned.length > 0) {
      formatted = cleaned.slice(0, 2);
    }
    if (cleaned.length > 2) {
      formatted += '.' + cleaned.slice(2, 4);
    }
    if (cleaned.length > 4) {
      formatted += '.' + cleaned.slice(4, 8);
    }
    if (formatted.length <= 10) {
      setDateInput(formatted);
      setError('');
    }
  }, []);

  const handleCalculate = useCallback(() => {
    const parts = dateInput.split('.');
    if (parts.length !== 3 || parts[2].length !== 4) {
      setError('Tarix formatı: GG.AA.İİİİ');
      return;
    }

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      setError('Düzgün tarix daxil edin');
      return;
    }
    if (day < 1 || day > 31) {
      setError('Gün 1-31 arasında olmalıdır');
      return;
    }
    if (month < 1 || month > 12) {
      setError('Ay 1-12 arasında olmalıdır');
      return;
    }
    if (year < 1000 || year > 2030) {
      setError('İl 1000-2030 arasında olmalıdır');
      return;
    }

    const result = calculateKarma(day, month, year);
    setResults(result);
    setBirthDate({ day, month, year });
    setError('');

    // If compatibility mode is enabled and 2nd date is valid, compute date-2 too
    if (compatEnabled && dateInput2) {
      const p2 = dateInput2.split('.');
      if (p2.length === 3 && p2[2].length === 4) {
        const d2 = parseInt(p2[0], 10);
        const m2 = parseInt(p2[1], 10);
        const y2 = parseInt(p2[2], 10);
        if (!isNaN(d2) && !isNaN(m2) && !isNaN(y2)
            && d2 >= 1 && d2 <= 31 && m2 >= 1 && m2 <= 12 && y2 >= 1000 && y2 <= 2030) {
          setResults2(calculateKarma(d2, m2, y2));
          setBirthDate2({ day: d2, month: m2, year: y2 });
        } else {
          setResults2(null);
          setBirthDate2(null);
        }
      } else {
        setResults2(null);
        setBirthDate2(null);
      }
    } else {
      setResults2(null);
      setBirthDate2(null);
    }
  }, [dateInput, compatEnabled, dateInput2]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleCalculate();
    }
  }, [handleCalculate]);

  // Compute effective connections: base + each enabled group's connections
  const effectiveConnections = useMemo(() => {
    const base = connections || [];
    const fromGroups = [];
    for (const g of (connectionGroups || [])) {
      const isEnabled = groupOverrides[g.id] !== undefined ? groupOverrides[g.id] : g.enabled;
      if (isEnabled && Array.isArray(g.connections)) {
        for (const c of g.connections) fromGroups.push(c);
      }
    }
    // De-dup
    const seen = new Set();
    const out = [];
    for (const c of [...base, ...fromGroups]) {
      const key = c[0] < c[1] ? `${c[0]}|${c[1]}` : `${c[1]}|${c[0]}`;
      if (!seen.has(key)) { seen.add(key); out.push(c); }
    }
    return out;
  }, [connections, connectionGroups, groupOverrides]);

  const toggleGroup = (id) => {
    const grp = connectionGroups.find(g => g.id === id);
    const currentlyEnabled = groupOverrides[id] !== undefined
      ? groupOverrides[id]
      : (grp ? grp.enabled : true);
    setGroupOverrides({ ...groupOverrides, [id]: !currentlyEnabled });
  };

  const isGroupEnabled = (g) =>
    groupOverrides[g.id] !== undefined ? groupOverrides[g.id] : g.enabled;

  const isPanelEnabled = (side) => {
    const p = panels?.[side];
    if (!p) return false;
    return panelOverrides[side] !== undefined ? panelOverrides[side] : p.enabled;
  };

  const togglePanel = (side) => {
    setPanelOverrides({ ...panelOverrides, [side]: !isPanelEnabled(side) });
  };

  // Build a map: connection-pair-key -> group color (only for ENABLED groups)
  // This allows the matrix to render group connections in their distinct color
  const connectionColorMap = useMemo(() => {
    const map = {};
    for (const g of (connectionGroups || [])) {
      if (!isGroupEnabled(g)) continue;
      const color = g.color || '#a855f7';
      for (const c of (g.connections || [])) {
        if (!Array.isArray(c) || c.length !== 2) continue;
        const key = c[0] < c[1] ? `${c[0]}|${c[1]}` : `${c[1]}|${c[0]}`;
        map[key] = color;
      }
    }
    return map;
  }, [connectionGroups, groupOverrides]);

  // Compute gate values from birth date
  const gateValues = useMemo(() => {
    if (!birthDate) return null;
    return calculateGates(birthDate.day, birthDate.month, birthDate.year);
  }, [birthDate]);

  // Elementwise Σ of the two dates' karma result maps — used for "Σ = X"
  // labels under spheres and for grid cells with date_source='sum'.
  // 22-rule is applied when the admin's `sumReduce22` flag is true.
  const sumResults = useMemo(() => {
    if (!compatEnabled || !sumEnabled || !results || !results2) return null;
    return sumKarmaResults(results, results2, { reduce: sumReduce22 });
  }, [compatEnabled, sumEnabled, sumReduce22, results, results2]);

  // Also compute Σ for custom nodes (compute per-node from filtered lists).
  const sumCustomNodes = useMemo(() => {
    if (!sumResults || !customNodes || !Array.isArray(customNodes)) return {};
    // Evaluate every enabled custom node's formula against d1_/d2_ tokens.
    // For each cn: value = cn.formula evaluated with pair injection.
    // We keep just the "main" (rounded, reduced or raw) value.
    const out = {};
    const pair = { date1: results, date2: results2 };
    for (const cn of customNodes) {
      if (!cn || cn.enabled === false) continue;
      const raw = String(cn.formula || '').trim();
      // Two supported cases:
      //  (a) formula uses base sphere tokens (A, HRT, ...) → sum of date1 + date2 evaluations
      //  (b) formula uses d1_XXX / d2_XXX → single evaluation with pair injection
      let val = null;
      if (/\bd1_|\bd2_/.test(raw)) {
        val = evaluateFormula(raw, results, { reduce: sumReduce22, pair });
      } else {
        const v1 = evaluateFormula(raw, results, { reduce: false });
        const v2 = evaluateFormula(raw, results2, { reduce: false });
        if (typeof v1 === 'number' && typeof v2 === 'number') {
          val = sumReduce22 ? sumKarmaResults({ x: v1 }, { x: v2 }, { reduce: true }).x : v1 + v2;
        }
      }
      if (typeof val === 'number' && isFinite(val)) out[cn.id] = val;
    }
    return out;
  }, [sumResults, customNodes, results, results2, sumReduce22]);

  // Render sections based on dynamic order
  const renderSection = (section, idx) => {
    // Backwards compat: section may be a plain string id
    const sec = typeof section === 'string'
      ? { id: section, type: section, title: '' }
      : section;
    const key = `${sec.id}-${idx}`;
    switch (sec.type) {
      case 'matrix': {
        // Compat-mode: render two matrices side-by-side (date-1, date-2) each
        // driven by its own results.  Custom nodes with date_source='secondary'
        // use results2; otherwise they use results.
        const buildMatrix = (r, keySuffix) => (
          <KarmaMatrix
            results={r}
            connections={effectiveConnections}
            nodeLabels={nodeLabels}
            nodePositions={nodePositions}
            nodeColors={nodeColors}
            nodeTextColors={nodeTextColors}
            nodeSizes={nodeSizes}
            nodeShapes={nodeShapes}
            globalSphereSize={globalSphereSize}
            backgroundImage={backgroundImage}
            backgroundScale={backgroundScale}
            backgroundOffsetX={backgroundOffsetX}
            backgroundOffsetY={backgroundOffsetY}
            backgroundLayers={backgroundLayers}
            backgroundFullscreen={compatEnabled && results2 ? false : backgroundFullscreen}
            paradiseMode={paradiseMode}
            hiddenSpheres={hiddenSpheres}
            hiddenValues={hiddenValues}
            connectionColorMap={connectionColorMap}
            connectionSpeed={connectionSpeed}
            connectionStyle={connectionStyle}
            gates={gates}
            gateValues={keySuffix === 2 && birthDate2
              ? calculateGates(birthDate2.day, birthDate2.month, birthDate2.year)
              : gateValues}
            customNodes={(customNodes || []).filter(cn =>
              !cn.date_source || cn.date_source === 'both' || cn.date_source === 'sum' ||
              (keySuffix === 1 ? cn.date_source === 'primary' : cn.date_source === 'secondary')
            )}
            evaluateFormula={evaluateFormula}
            sumValues={sumResults ? { ...sumResults, ...sumCustomNodes } : null}
            showSum={!!(compatEnabled && sumEnabled && sumResults)}
          />
        );
        if (compatEnabled && results2) {
          return (
            <div key={key} className="dynamic-section" data-testid={`section-matrix-dual-${sec.id}`}>
              {sec.title && <h2 className="dynamic-section-title">{sec.title}</h2>}
              <div className="dual-matrix-wrap">
                <div className="dual-matrix-col" data-testid="matrix-date-1">
                  <div className="dual-matrix-heading">1-ci tarix: {birthDate?.day}.{birthDate?.month}.{birthDate?.year}</div>
                  {buildMatrix(results, 1)}
                </div>
                <div className="dual-matrix-col" data-testid="matrix-date-2">
                  <div className="dual-matrix-heading">2-ci tarix: {birthDate2?.day}.{birthDate2?.month}.{birthDate2?.year}</div>
                  {buildMatrix(results2, 2)}
                </div>
              </div>
            </div>
          );
        }
        return <div key={key} className="dynamic-section">
          {sec.title && <h2 className="dynamic-section-title">{sec.title}</h2>}
          {buildMatrix(results, 1)}
        </div>;
      }
      case 'table':
        return <div key={key} className="dynamic-section">
          {sec.title && <h2 className="dynamic-section-title">{sec.title}</h2>}
          <KarmaTable
            results={results}
            rowOrder={tableRowOrder}
            hiddenRows={hiddenRows}
            hiddenColumns={hiddenColumns}
            customRows={customRows}
            nodeLabels={nodeLabels}
            evaluateFormula={evaluateFormula}
          />
        </div>;
      case 'special':
        return <div key={key} className="dynamic-section">
          {sec.title && <h2 className="dynamic-section-title">{sec.title}</h2>}
          <KarmaTable results={results} rowOrder={tableRowOrder} specialOnly />
        </div>;
      case 'text':
        return (
          <div key={key} className="dynamic-section dynamic-text-section" data-testid={`section-text-${sec.id}`}>
            {sec.title && <h2 className="dynamic-section-title">{sec.title}</h2>}
            {sec.content && (
              <div className="dynamic-text-content" style={{ whiteSpace: 'pre-wrap' }}>
                {sec.content}
              </div>
            )}
          </div>
        );
      case 'energy':
        return (
          <div key={key} className="dynamic-section" data-testid={`section-energy-${sec.id}`}>
            {sec.title && <h2 className="dynamic-section-title">{sec.title}</h2>}
            <EnergyCalculator config={energyCalc} birthDate={birthDate} testIdPrefix={`energy-${sec.id}`} />
          </div>
        );
      case 'grid':
        if (compatEnabled && results2 && birthDate2) {
          // In dual-date mode, sum cells appear in BOTH columns because
          // their value is a single combined figure the user wants visible.
          const filterCellsFor = (which) =>
            (gridCells || []).filter((c) => {
              const ds = c.date_source || 'primary';
              return ds === 'both' || ds === 'sum' || ds === which;
            });
          const cells1 = filterCellsFor('primary');
          const cells2 = filterCellsFor('secondary');
          const d1 = birthDate?.day ? parseInt(birthDate.day, 10) : null;
          const m1 = birthDate?.month ? parseInt(birthDate.month, 10) : null;
          const y1 = birthDate?.year ? parseInt(birthDate.year, 10) : null;
          const d2 = birthDate2?.day ? parseInt(birthDate2.day, 10) : null;
          const m2 = birthDate2?.month ? parseInt(birthDate2.month, 10) : null;
          const y2 = birthDate2?.year ? parseInt(birthDate2.year, 10) : null;
          return (
            <div key={key} className="dynamic-section" data-testid={`section-grid-dual-${sec.id}`}>
              {sec.title && <h2 className="dynamic-section-title">{sec.title}</h2>}
              <div className="dual-matrix-wrap">
                <div className="dual-matrix-col" data-testid="grid-date-1">
                  <div className="dual-matrix-heading">1-ci tarix: {birthDate?.day}.{birthDate?.month}.{birthDate?.year}</div>
                  <GridCellsRenderer
                    cells={cells1}
                    results={results}
                    day={d1}
                    month={m1}
                    year={y1}
                    results2={results2}
                    day2={d2}
                    month2={m2}
                    year2={y2}
                    resultsSum={sumResults}
                    gridBgImage={gridBgImage}
                    gridTitle={gridTitle}
                    popupsEnabled={gridPopupsEnabled}
                  />
                </div>
                <div className="dual-matrix-col" data-testid="grid-date-2">
                  <div className="dual-matrix-heading">2-ci tarix: {birthDate2?.day}.{birthDate2?.month}.{birthDate2?.year}</div>
                  <GridCellsRenderer
                    cells={cells2}
                    results={results2}
                    day={d2}
                    month={m2}
                    year={y2}
                    results2={results}
                    day2={d1}
                    month2={m1}
                    year2={y1}
                    resultsSum={sumResults}
                    gridBgImage={gridBgImage}
                    gridTitle={gridTitle}
                    popupsEnabled={gridPopupsEnabled}
                  />
                </div>
              </div>
            </div>
          );
        }
        return (
          <div key={key} className="dynamic-section" data-testid={`section-grid-${sec.id}`}>
            {sec.title && <h2 className="dynamic-section-title">{sec.title}</h2>}
            <GridCellsRenderer
              cells={gridCells}
              results={results}
              day={birthDate?.day ? parseInt(birthDate.day, 10) : null}
              month={birthDate?.month ? parseInt(birthDate.month, 10) : null}
              year={birthDate?.year ? parseInt(birthDate.year, 10) : null}
              gridBgImage={gridBgImage}
              gridTitle={gridTitle}
              popupsEnabled={gridPopupsEnabled}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="karma-page">
      {/* Loading veil — covers the page until config arrives so users never
          see the React default state flash before the admin-saved values render.
          Sits above everything but fades out cleanly when ready. */}
      <div
        aria-hidden="true"
        data-testid="config-loading-veil"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(180deg, #050510 0%, #0a0520 30%, #0d0a25 60%, #050510 100%)',
          zIndex: 9999,
          opacity: configReady ? 0 : 1,
          pointerEvents: configReady ? 'none' : 'auto',
          transition: 'opacity 220ms ease-out',
        }}
      />
      {/* Fullscreen background layers (scrolls with page content) */}
      {backgroundFullscreen && (backgroundLayers || []).length > 0 && (
        <div
          className="fullscreen-bg-layers"
          aria-hidden="true"
          data-testid="fullscreen-bg-layers"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          {(backgroundLayers || []).map((layer, i) => {
            const url = `${BACKEND_URL}/api/uploads/${layer.filename}`;
            const sc = layer.scale ?? 1;
            const ox = layer.offset_x ?? 0;
            const oy = layer.offset_y ?? 0;
            const op = layer.opacity ?? 1;
            const fit = layer.fit_mode || 'cover';
            // Map fit modes to CSS background-size / object-fit equivalents
            let objectFit = 'cover';
            if (fit === 'contain') objectFit = 'contain';
            else if (fit === 'stretch') objectFit = 'fill';
            else if (fit === 'natural') objectFit = 'none';
            return (
              <img
                key={`fbg-${i}-${layer.filename}`}
                src={url}
                alt=""
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                fetchPriority={i === 0 ? 'high' : 'auto'}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  width: '100%',
                  height: '100vh',
                  objectFit,
                  transform: `translateX(-50%) translate(${ox}px, ${oy}px) scale(${sc})`,
                  opacity: op,
                  transformOrigin: 'center top',
                  willChange: 'transform',
                }}
              />
            );
          })}
        </div>
      )}

      {/* Star field background */}
      <div className="star-field">
        {stars.map(star => (
          <div
            key={star.id}
            className="star"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
              opacity: star.opacity,
            }}
          />
        ))}
      </div>

      {/* Nebula effects */}
      <div className="nebula nebula-1" />
      <div className="nebula nebula-2" />
      <div className="nebula nebula-3" />

      {/* Animated cosmic sun at the top */}
      <div
        className="cosmic-sun"
        aria-hidden="true"
        data-testid="cosmic-sun"
        style={{
          left: `${sunX}%`,
          top: `${sunY}px`,
          transform: `translateX(-50%) scale(${sunScale})`,
          filter: `brightness(${sunBrightness})`,
        }}
      >
        <div className="sun-corona" />
        <div className="sun-rays" />
        <div className="sun-core" />
        <div className="sun-glow" />
      </div>

      {/* Admin gear button */}
      <button
        className="admin-gear-btn"
        onClick={() => navigate('/admin')}
        title="Admin Panel"
      >
        <Settings size={18} />
      </button>

      {/* Side panels (Soy Hafızası / Yaşam Sahneleri) */}
      {!hideSidePanels && panels?.left && isPanelEnabled('left') && (
        <div className="side-panel side-panel-left" data-testid="side-panel-left">
          <div className="side-panel-doors">
            {(panels.left.doors || []).map((d, i) => (
              <div key={`l-${i}`} className="side-door" data-testid={`left-door-${i}`}>
                <span className="side-door-number">{d}</span>
              </div>
            ))}
          </div>
          <div className="side-panel-text">
            {panels.left.title && (
              <h3 className="side-panel-title">{panels.left.title}</h3>
            )}
            {panels.left.subtitle && (
              <p className="side-panel-subtitle">{panels.left.subtitle}</p>
            )}
          </div>
        </div>
      )}

      {!hideSidePanels && panels?.right && isPanelEnabled('right') && (
        <div className="side-panel side-panel-right" data-testid="side-panel-right">
          <div className="side-panel-doors">
            {(panels.right.doors || []).map((d, i) => (
              <div key={`r-${i}`} className="side-door" data-testid={`right-door-${i}`}>
                <span className="side-door-number">{d}</span>
              </div>
            ))}
          </div>
          <div className="side-panel-text">
            {panels.right.title && (
              <h3 className="side-panel-title">{panels.right.title}</h3>
            )}
            {panels.right.subtitle && (
              <p className="side-panel-subtitle">{panels.right.subtitle}</p>
            )}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="karma-content-wrap relative z-10 flex flex-col items-center min-h-screen px-4 py-8" style={{ paddingBottom: '120px' }}>
        {/* Title */}
        <h1 className="karma-title" data-testid="karma-title">
          {title || t('main.title')}
        </h1>
        {(subtitle || t('main.subtitle')) && (
          <p className="karma-subtitle" data-testid="karma-subtitle" style={{ whiteSpace: 'pre-line' }}>
            {subtitle || t('main.subtitle')}
          </p>
        )}

        {/* Input section */}
        <div className="input-section">
          <input
            type="text"
            className="karma-input"
            placeholder={t('main.datePlaceholder')}
            value={dateInput}
            onChange={handleDateChange}
            onKeyDown={handleKeyDown}
            maxLength={10}
            data-testid="date-input"
          />
          <button className="karma-button" onClick={handleCalculate} data-testid="calc-btn">
            {calculateButtonText || t('common.calculate')}
          </button>
        </div>

        {/* Il Enerji Uyumu (Compatibility Mode) — Student/User pages only,
            and only when admin allows it (compat_visible !== false). */}
        {(pageMode === 'student' || pageMode === 'user') && compatVisible && (
          <div className="compat-toggle-wrap" data-testid="compat-toggle-wrap">
            <label className="compat-toggle-label">
              <input
                type="checkbox"
                checked={compatEnabled}
                onChange={(e) => {
                  setCompatEnabled(e.target.checked);
                  if (!e.target.checked) {
                    setResults2(null);
                    setBirthDate2(null);
                    setDateInput2('');
                  }
                }}
                data-testid="compat-toggle"
              />
              <span>Il Enerji Uyumunu yoxla (2-ci doğum tarixi ilə)</span>
            </label>
            {compatEnabled && (
              <div className="compat-inputs" data-testid="compat-inputs">
                <input
                  type="text"
                  className="karma-input compat-date-input"
                  placeholder="2-ci tarix: GG.AA.İİİİ"
                  value={dateInput2}
                  onChange={(e) => {
                    // Auto-format DD.MM.YYYY like the primary field
                    let v = e.target.value.replace(/\D/g, '');
                    if (v.length > 2) v = v.slice(0, 2) + '.' + v.slice(2);
                    if (v.length > 5) v = v.slice(0, 5) + '.' + v.slice(5, 9);
                    setDateInput2(v);
                  }}
                  maxLength={10}
                  data-testid="date-input-2"
                />
                <button
                  className="karma-button compat-btn"
                  onClick={handleCalculate}
                  data-testid="compat-check-btn"
                >
                  Uyumu yoxla
                </button>
              </div>
            )}
          </div>
        )}

        {error && <p className="error-text">{error}</p>}

        {/* Groups toggle bar — extract into a variable so we can inject it
            at 'above' / 'below-matrix' / 'below-grid' per admin choice. */}
        {(() => {
          const groupsBar = !hideGroupsBar && ((connectionGroups && connectionGroups.length > 0) || panels?.left || panels?.right) ? (
            <div className="conn-groups-bar" data-testid="conn-groups-bar">
              {panels?.left && (
                <button
                  onClick={() => togglePanel('left')}
                  className={`conn-group-toggle ${isPanelEnabled('left') ? 'conn-group-toggle-on' : ''}`}
                  data-testid="panel-toggle-left"
                  title="Soy Hafızası panelini aç/bağla"
                >
                  <span className="conn-group-dot" />
                  <span className="conn-group-name">{panels.left.title || 'Sol panel'}</span>
                </button>
              )}
              {panels?.right && (
                <button
                  onClick={() => togglePanel('right')}
                  className={`conn-group-toggle ${isPanelEnabled('right') ? 'conn-group-toggle-on' : ''}`}
                  data-testid="panel-toggle-right"
                  title="Yaşam Sahneleri panelini aç/bağla"
                >
                  <span className="conn-group-dot" />
                  <span className="conn-group-name">{panels.right.title || 'Sağ panel'}</span>
                </button>
              )}
              {connectionGroups.map(g => {
                const enabled = isGroupEnabled(g);
                const groupColor = g.color || '#a855f7';
                return (
                  <button
                    key={g.id}
                    onClick={() => toggleGroup(g.id)}
                    className={`conn-group-toggle ${enabled ? 'conn-group-toggle-on' : ''}`}
                    data-testid={`conn-group-toggle-${g.id}`}
                    title={enabled ? 'Aktiv (klikləyib bağla)' : 'Deaktiv (klikləyib aç)'}
                    style={enabled ? {
                      borderColor: groupColor,
                      color: groupColor,
                      boxShadow: `0 0 14px ${groupColor}55, inset 0 0 12px ${groupColor}22`,
                    } : undefined}
                  >
                    <span
                      className="conn-group-dot"
                      style={enabled ? { background: groupColor, boxShadow: `0 0 8px ${groupColor}` } : undefined}
                    />
                    <span className="conn-group-name">{g.name}</span>
                    {g.connections && g.connections.length > 0 && (
                      <span className="conn-group-count">{g.connections.length}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : null;

          const sectionList = (sections != null ? sections : sectionOrder);
          const rendered = [];
          if (groupsPosition === 'above' && groupsBar) rendered.push(<React.Fragment key="grp-above">{groupsBar}</React.Fragment>);
          let matrixInjected = false;
          sectionList.forEach((sec, idx) => {
            rendered.push(renderSection(sec, idx));
            const secType = typeof sec === 'string' ? sec : sec?.type;
            if (groupsPosition === 'below-matrix' && !matrixInjected && secType === 'matrix' && groupsBar) {
              rendered.push(<React.Fragment key={`grp-below-matrix-${idx}`}>{groupsBar}</React.Fragment>);
              matrixInjected = true;
            }
          });
          return rendered;
        })()}

        {/* Variants — extra matrix diagrams rendered with the same birth date */}
        {results && variants && variants.length > 0 && (
          <VariantsSection
            parentPage={pageMode === 'student' ? 'student' : (pageMode === 'user' && urlSlug ? `user_${urlSlug}` : 'main')}
            variants={variants}
            layout={variantsLayout}
            results={results}
            day={birthDate?.day ? parseInt(birthDate.day, 10) : null}
            month={birthDate?.month ? parseInt(birthDate.month, 10) : null}
            year={birthDate?.year ? parseInt(birthDate.year, 10) : null}
            compatEnabled={compatEnabled}
            results2={results2}
            day2={birthDate2?.day ? parseInt(birthDate2.day, 10) : null}
            month2={birthDate2?.month ? parseInt(birthDate2.month, 10) : null}
            year2={birthDate2?.year ? parseInt(birthDate2.year, 10) : null}
            birthDate={birthDate}
            birthDate2={birthDate2}
            sumEnabled={sumEnabled}
            sumReduce22={sumReduce22}
            authHeaders={pageMode === 'student' ? { Authorization: `Bearer ${localStorage.getItem('student_token') || ''}` } : {}}
          />
        )}

        {/* AI interpretation card — auto-renders after a calculation */}
        {aiEnabled && results && birthDate && (
          <AIInterpretation
            results={results}
            birthDate={birthDate}
            gates={gateValues}
            gateNames={gates?.names || null}
            language={i18n.language}
          />
        )}

        {/* Challenge / Childhood Code interpretation — after AI card */}
        {results && results.I && (
          <ChallengeInterpretation
            challenges={challenges}
            sectionTitle={challengeSectionTitle}
            kd1Value={results.I}
            kd1Add={results.I1}
            language={i18n.language}
            enabled={challengesEnabled}
          />
        )}

        {/* Connection group toggles + Side panel toggles — bottom position
            (default). Only rendered when groupsPosition === 'below-grid'. */}
        {groupsPosition === 'below-grid' && !hideGroupsBar && ((connectionGroups && connectionGroups.length > 0) || panels?.left || panels?.right) && (
          <div className="conn-groups-bar" data-testid="conn-groups-bar">
            {panels?.left && (
              <button
                onClick={() => togglePanel('left')}
                className={`conn-group-toggle ${isPanelEnabled('left') ? 'conn-group-toggle-on' : ''}`}
                data-testid="panel-toggle-left"
                title="Soy Hafızası panelini aç/bağla"
              >
                <span className="conn-group-dot" />
                <span className="conn-group-name">{panels.left.title || 'Sol panel'}</span>
              </button>
            )}
            {panels?.right && (
              <button
                onClick={() => togglePanel('right')}
                className={`conn-group-toggle ${isPanelEnabled('right') ? 'conn-group-toggle-on' : ''}`}
                data-testid="panel-toggle-right"
                title="Yaşam Sahneleri panelini aç/bağla"
              >
                <span className="conn-group-dot" />
                <span className="conn-group-name">{panels.right.title || 'Sağ panel'}</span>
              </button>
            )}
            {connectionGroups.map(g => {
              const enabled = isGroupEnabled(g);
              const groupColor = g.color || '#a855f7';
              return (
                <button
                  key={g.id}
                  onClick={() => toggleGroup(g.id)}
                  className={`conn-group-toggle ${enabled ? 'conn-group-toggle-on' : ''}`}
                  data-testid={`conn-group-toggle-${g.id}`}
                  title={enabled ? 'Aktiv (klikləyib bağla)' : 'Deaktiv (klikləyib aç)'}
                  style={enabled ? {
                    borderColor: groupColor,
                    color: groupColor,
                    boxShadow: `0 0 14px ${groupColor}55, inset 0 0 12px ${groupColor}22`,
                  } : undefined}
                >
                  <span
                    className="conn-group-dot"
                    style={enabled ? { background: groupColor, boxShadow: `0 0 8px ${groupColor}` } : undefined}
                  />
                  <span className="conn-group-name">{g.name}</span>
                  {g.connections && g.connections.length > 0 && (
                    <span className="conn-group-count">{g.connections.length}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <footer className="karma-footer">
          {subtitle || 'Karma Matrisi Kalkulyatoru'}
        </footer>

        {/* Custom positionable footer text (admin-configurable, drag-positioned in admin) */}
        {footerVisible && footerText && (
          <div
            className="karma-custom-footer"
            data-testid="karma-custom-footer"
            style={{
              position: 'absolute',
              left: `${footerX}%`,
              top: `${footerY}%`,
              transform: 'translate(-50%, -50%)',
              maxWidth: '90%',
              textAlign: 'center',
              fontSize: `${footerFontSize}px`,
              color: footerColor,
              whiteSpace: 'pre-line',
              pointerEvents: 'none',
              fontFamily: "'Cinzel', serif",
              textShadow: '0 1px 3px rgba(0,0,0,0.6)',
              lineHeight: 1.5,
              zIndex: 5,
            }}
          >
            {footerText}
          </div>
        )}

        {/* Floating contact buttons (WhatsApp / Instagram / Telegram / Email) */}
        <ContactButtons
          whatsapp={{ enabled: whatsappEnabled, number: whatsappNumber, message: whatsappMessage, label: whatsappLabel }}
          instagram={{ enabled: instagramEnabled, username: instagramUsername, label: 'Instagram' }}
          telegram={{ enabled: telegramEnabled, username: telegramUsername, label: 'Telegram' }}
          email={{ enabled: emailEnabled, address: emailAddress, subject: emailSubject, label: 'Email' }}
        />

        {/* Support chat widget (separate floating bubble, bottom-right) */}
        <SupportChat
          enabled={supportChatEnabled}
          welcomeMessage={supportWelcomeMessage}
        />
      </div>
    </div>
  );
};

export default KarmaCalculator;
